import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { logError } from '@/api/backend';
import { cutlistsApi } from '@/api/backend/cutlists';
import { useNestingFailureMessage } from '@/api/backend/nestingErrors';
import { usePartsErrorStore } from '@/api/errors';
import {
  useActiveGroup,
  useMaterialGroups,
  useMaterialGroupState,
} from '@/api/materialsGroup';
import {
  computeCutlistNestingHash,
  isNestingPollSettled,
  useMixedNestingStore,
} from '@/api/mixedNesting';
import { usePricingStore } from '@/api/pricing';
import {
  isValidPart,
  partsSelector,
  useCutlistParts,
  useCutlistState,
} from '@/api/store';
import Card from '@/blocks/Card';
import { FullPriceDataCard } from '@/blocks/Price';
import { useManualEdgebanding } from '@/hooks';
import {
  CalculatePriceButton,
  StarterTierNesting,
} from '@/interfaces/StarterTierNesting';
import { Button } from '@/primitives/Button';
import {
  getStarterCutlistQuery,
  starterKeys,
  usePollNestingJobs,
} from '@/starter-tier/queries';
import { useCurrentFeatures, useTicker } from '@/theme';
import { currencyFormatter } from '@/utils/format';

import { InfoMessage } from './InfoMessage';
import { StarterLeadModal } from './StarterLeadModal';

const snapshotLiveHash = () =>
  computeCutlistNestingHash(
    useMaterialGroupState.getState().groups,
    partsSelector(useCutlistState.getState())
  );

export const StarterAside = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const source = useTicker();
  const { id } = useParams();
  const cutlistId = id as string;
  const [isLeadModalOpen, setIsLeadModalOpen] = React.useState(false);

  const groups = useMaterialGroups();
  const parts = useCutlistParts();
  const activeGroup = useActiveGroup();
  const isBeamGroup = activeGroup?.type === 'beams';

  const { showPricing, manualQuote, totalAmountExclVAT, totalAmountInclVAT } =
    usePricingStore();
  const { currency } = useCutlistState();
  const { vatDisplayMode } = useCurrentFeatures();
  const setMixedResults = useMixedNestingStore((state) => state.setResults);

  const nestedGroups = useMixedNestingStore((state) =>
    state.groups.filter((group) => group.id === activeGroup?.id)
  );

  const nestingState = useMixedNestingStore((state) => state.nestingState);
  const snapshotHash = useMixedNestingStore((state) => state.snapshotHash);
  const { setNestingState, setSnapshotHash, setPolling, setFailedGroups } =
    useMixedNestingStore.getState();

  const { data: pollData, isError: pollError } = usePollNestingJobs(cutlistId);

  const failureMessage = useNestingFailureMessage(activeGroup?.id);

  const liveHash = computeCutlistNestingHash(groups, parts);
  const dirty = snapshotHash !== null && liveHash !== snapshotHash;

  const nestableGroups = groups.filter(
    (group) =>
      group.type !== 'beams' &&
      parts.some((part) => part.groupId === group.id && isValidPart(part))
  );
  const hasSomethingToCompute =
    nestableGroups.length > 0 ||
    parts.some((part) => part.partType === 'beam' && part.beamMaterialId);

  React.useEffect(() => {
    if (pollError) {
      setFailedGroups(null);
      setPolling(false);
      setNestingState('failed');
      return;
    }

    if (!pollData) return;

    if (pollData.done) {
      const { cutlist } = pollData;
      queryClient.setQueryData(starterKeys.getOne(cutlistId), cutlist);
      setMixedResults(cutlist);

      const pricing = usePricingStore.getState();
      pricing.setError(null);
      pricing.setAmountVat(cutlist.totalAmountInclVAT);
      pricing.setAmountNoVat(cutlist.totalAmountExclVAT);
      pricing.setPricing(cutlist.pricingLineItems);
      pricing.toggleManualQuote(cutlist.manualQuote);

      usePartsErrorStore.getState().resetPartsNestingError();
      setPolling(false);
      setNestingState('done');
      return;
    }

    const failedGroups = pollData.groups.filter(
      (group) => group.status === 'failed'
    );
    if (!failedGroups.length || !isNestingPollSettled(pollData)) return;

    setFailedGroups(failedGroups);
    setPolling(false);
    setNestingState('failed');
    queryClient
      .fetchQuery(getStarterCutlistQuery(cutlistId))
      .then(setMixedResults)
      .catch(logError);
  }, [
    pollData,
    pollError,
    t,
    setMixedResults,
    setNestingState,
    setPolling,
    setFailedGroups,
    queryClient,
    cutlistId,
  ]);

  const runNesting = async () => {
    setNestingState('computing');
    usePartsErrorStore.getState().resetPartsNestingError();
    try {
      await cutlistsApi.saveStarterTierCutlist(cutlistId);
      toast.success(t('cutlist-summary.nesting.saved'));
      const snapshotHash = snapshotLiveHash();
      setSnapshotHash(snapshotHash);

      await cutlistsApi.triggerNestingJobs(cutlistId);

      queryClient.removeQueries({
        queryKey: starterKeys.nestingJobs(cutlistId),
      });
      setPolling(true);
    } catch {
      setFailedGroups(null);
      setPolling(false);
      setNestingState('failed');
    }
  };

  const goNext = () => {
    navigate(`/cutlist/${cutlistId}/details`);
    window.analytics.track('Navigated to contact details', { ticker: source });
  };

  const isComputing = nestingState === 'computing';
  const canNavigate = nestingState === 'done' && !dirty;
  const { isBlocked: isManualEdgebandingBlocked } = useManualEdgebanding();

  const totalAmount =
    vatDisplayMode === 'exclusive' ? totalAmountExclVAT : totalAmountInclVAT;
  const reviewButtonLabel = canNavigate
    ? t(
        manualQuote
          ? 'common.cta.nextWithPriceRequiresManualQuoting'
          : 'common.cta.nextWithPrice',
        { totalAmount: currencyFormatter(totalAmount / 100, currency) }
      )
    : t('common.cta.nextToDeliveryDetails');

  const calculatePriceButton = !canNavigate && (
    <CalculatePriceButton
      onCalculate={runNesting}
      canCalculate={hasSomethingToCompute}
      isComputing={isComputing}
      small
    />
  );

  return (
    <aside>
      {!isBeamGroup && (
        <StarterTierNesting
          groups={nestedGroups}
          canCalculate={hasSomethingToCompute}
          isComputing={isComputing}
          dirty={dirty}
          failureMessage={failureMessage}
        />
      )}
      <Card>
        {isBeamGroup && failureMessage && (
          <InfoMessage message={failureMessage} variant="error" />
        )}
        {showPricing ? (
          <FullPriceDataCard
            title={
              manualQuote
                ? t('cutlist-summary.price.estimated.title')
                : t('cutlist-summary.price.total.title')
            }
            action={calculatePriceButton || undefined}
            hidePrice={!canNavigate}
          />
        ) : (
          calculatePriceButton
        )}
        <div className="stack gap-xs">
          <Button
            name="theNextButton"
            onClick={goNext}
            disabled={!canNavigate || isManualEdgebandingBlocked}
          >
            {reviewButtonLabel}
          </Button>
          <Button
            className="outline"
            name="starterLeadButton"
            onClick={() => setIsLeadModalOpen(true)}
          >
            {t('landing-page.starter-lead.cta')}
          </Button>
        </div>
      </Card>
      <StarterLeadModal
        cutlistId={cutlistId}
        isOpen={isLeadModalOpen}
        onClose={() => setIsLeadModalOpen(false)}
      />
    </aside>
  );
};
